import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Filter, X } from 'lucide-react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  InstantSearchProvider,
  SearchBox,
  Hits,
  RefinementList,
  Pagination,
  SortBy,
  Stats,
  CurrentRefinements,
} from '@/components/search';

const SORT_OPTIONS = [
  { value: 'displays', label: 'Relevance' },
  { value: 'displays/sort/featured:desc', label: 'Featured First' },
  { value: 'displays/sort/created_at:desc', label: 'Newest First' },
  { value: 'displays/sort/display_name:asc', label: 'Name A-Z' },
  { value: 'displays/sort/city:asc', label: 'City A-Z' },
];

const DisplaysInstantSearch = () => {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <>
      <Helmet>
        <title>Light Display Map - Find Holiday Light Shows</title>
        <meta 
          name="description" 
          content="Find and explore holiday light displays in your area. Browse Christmas, Halloween, and other seasonal light shows." 
        />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        
        <InstantSearchProvider indexName="displays" routing={true}>
          <main className="flex-1">
            {/* Search Header */}
            <div className="bg-muted/30 border-b">
              <div className="container py-6">
                <div className="max-w-4xl mx-auto">
                  <h1 className="text-3xl font-bold mb-4">Light Display Map</h1>
                  <div className="flex gap-2">
                    <SearchBox 
                      placeholder="Search displays, locations..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="shrink-0 lg:hidden"
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      Filters
                    </Button>
                  </div>

                  {/* Search Stats and Sort */}
                  <div className="flex items-center justify-between mt-4 text-sm">
                    <Stats />
                    <SortBy items={SORT_OPTIONS} />
                  </div>
                </div>
              </div>
            </div>

            {/* Active Filters */}
            <div className="container py-4">
              <CurrentRefinements />
            </div>

            <div className="container">
              <div className="flex gap-6">
                {/* Filters Sidebar */}
                <aside className={cn(
                  "w-80 shrink-0 sticky top-4 h-fit",
                  !showFilters && "hidden lg:block"
                )}>
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Filters</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {/* State Filter */}
                      <RefinementList
                        attribute="state"
                        title="State"
                        expanded={true}
                        limit={20}
                        searchable={true}
                        searchablePlaceholder="Search states..."
                      />
                      
                      <Separator className="my-4" />
                      
                      {/* City Filter */}
                      <RefinementList
                        attribute="city"
                        title="City"
                        expanded={false}
                        limit={20}
                        searchable={true}
                        searchablePlaceholder="Search cities..."
                      />
                      
                      <Separator className="my-4" />
                      
                      {/* Country Filter */}
                      <RefinementList
                        attribute="country"
                        title="Country"
                        expanded={false}
                        limit={10}
                      />

                      <Separator className="my-4" />

                      {/* Featured Filter */}
                      <RefinementList
                        attribute="featured"
                        title="Featured"
                        expanded={true}
                        limit={5}
                      />
                    </CardContent>
                  </Card>
                </aside>

                {/* Results */}
                <div className="flex-1 min-w-0 space-y-6">
                  <Hits />
                  <Pagination className="pb-8" />
                </div>
              </div>

              {/* Mobile Filters Overlay */}
              {showFilters && (
                <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-xs">
                  <div className="fixed inset-y-0 left-0 w-80 bg-background border-r overflow-y-auto">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">Filters</h2>
                        <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-4">
                        <RefinementList
                          attribute="state"
                          title="State"
                          expanded={true}
                          limit={20}
                          searchable={true}
                        />
                        <RefinementList
                          attribute="city"
                          title="City"
                          expanded={false}
                          limit={20}
                          searchable={true}
                        />
                        <RefinementList
                          attribute="country"
                          title="Country"
                          expanded={false}
                          limit={10}
                        />
                        <RefinementList
                          attribute="featured"
                          title="Featured"
                          expanded={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </InstantSearchProvider>
        
        <Footer />
      </div>
    </>
  );
};

export default DisplaysInstantSearch;